import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchThemeData } from "../../admin/themeSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { getLandingPageTitleColor } from "../../functions/themeAssets";
import GameStageLayout from "./GameStageLayout";
import "./gameStageLayout.css";
import "../fonts/breuer-headline.css";
import "./quizStage.css";
import "./quizImageCard.css";
import QuizImageCard from "./QuizImageCard";
import {
  getImageOnlyPointsForChoice,
  parseImageOnlyFromTheme,
} from "./stage5ImageOnlyUtils";
import {
  STAGE_ROW_KEY,
  clearStageActive,
  getRedirectForPlayedStage,
  hydrateCompletedStagesFromRecord,
  markStageCompleted,
  saveGameStageAndReport,
  setStageActive,
  updateExternalReport,
} from "../../functions/stageReportSync";
import { showStageCompleteCelebration, navigateAfterCelebration } from "../../functions/stageCelebration";
import {
  fireStageHelpSwal,
  getStageRulesFromTheme,
} from "../../functions/stageRules";

const STAGE_NUM = 5;

/** 1–5 cards: one centered row; 6+: split evenly across two rows (e.g. 6 → 3+3). */
function getQuizCardColumnCount(count) {
  if (count <= 0) return 1;
  if (count <= 5) return count;
  return Math.ceil(count / 2);
}

function parseClockToSeconds(clock) {
  const parts = String(clock ?? "00:00")
    .trim()
    .split(":")
    .map((p) => parseInt(p, 10));
  if (parts.some((n) => !Number.isFinite(n))) return 0;
  if (parts.length >= 3) return parts[0] * 3600 + parts[1] * 60 + parts[2];
  if (parts.length >= 2) return parts[0] * 60 + parts[1];
  return parts[0] || 0;
}

function formatSecondsToClock(totalSec) {
  const sec = Math.max(0, Math.floor(totalSec));
  const min = Math.floor(sec / 60);
  const rem = sec % 60;
  return `${String(min).padStart(2, "0")}:${String(rem).padStart(2, "0")}`;
}

function getStoredUser() {
  try {
    return JSON.parse(sessionStorage.getItem("userData") || "{}");
  } catch {
    return {};
  }
}

export default function Stage5() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status: themeStatus, data: themeData } = useSelector((s) => s.theme);

  const [dbReady, setDbReady] = useState(false);
  const [elapsedSec, setElapsedSec] = useState(0);
  const [stage5Points, setStage5Points] = useState(0);
  const [displayTotalScore, setDisplayTotalScore] = useState(0);
  const [selections, setSelections] = useState({});
  const [submitting, setSubmitting] = useState(false);

  const elapsedRef = useRef(0);
  const baseElapsedRef = useRef(0);
  const sessionStartRef = useRef(null);
  const otherStagesTotalRef = useRef(0);
  const submittedRef = useRef(false);
  const timerStartedRef = useRef(false);

  const storedUser = useMemo(() => getStoredUser(), []);
  const isDemoBypass = storedUser.sessionId === "demobypass";
  const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

  const questions = useMemo(() => parseImageOnlyFromTheme(themeData), [themeData]);

  const title = useMemo(() => {
    const t =
      themeData?.image_only_title ||
      themeData?.landing_page_title ||
      themeData?.main_title ||
      themeData?.themename ||
      "Image Only";
    return String(t).trim();
  }, [themeData]);

  const subtitle = useMemo(() => {
    const custom = themeData?.image_only_subtitle;
    if (custom && String(custom).trim()) return String(custom).trim();
    return "Look at each image clue and choose the matching answer.";
  }, [themeData]);

  const titleTextColor = useMemo(() => getLandingPageTitleColor(themeData), [themeData]);
  const themedTextStyle = useMemo(
    () => (titleTextColor ? { color: titleTextColor } : undefined),
    [titleTextColor]
  );

  const pointsPerQuestion = useMemo(() => {
    const themeDefault = Number(themeData?.points);
    return Number.isFinite(themeDefault) && themeDefault > 0
      ? Math.trunc(themeDefault)
      : 1;
  }, [themeData?.points]);

  const answeredCount = useMemo(
    () => Object.keys(selections).filter((k) => selections[k]).length,
    [selections]
  );

  const allAnswered = questions.length > 0 && answeredCount === questions.length;

  const cardCols = useMemo(
    () => getQuizCardColumnCount(questions.length),
    [questions.length]
  );

  useEffect(() => {
    dispatch(fetchThemeData({ themeId: null }));
  }, [dispatch]);

  useEffect(() => {
    document.body.classList.add("quiz-page");
    return () => {
      document.body.classList.remove("quiz-page");
    };
  }, []);

  useEffect(() => {
    if (!dbReady || timerStartedRef.current) return;
    timerStartedRef.current = true;
    sessionStartRef.current = Date.now();

    const tick = setInterval(() => {
      const sessionSec = Math.floor((Date.now() - sessionStartRef.current) / 1000);
      const elapsed = baseElapsedRef.current + sessionSec;
      elapsedRef.current = elapsed;
      setElapsedSec(elapsed);
    }, 1000);

    return () => clearInterval(tick);
  }, [dbReady]);

  useEffect(() => {
    setDisplayTotalScore(otherStagesTotalRef.current + stage5Points);
  }, [stage5Points]);

  const finishStage = useCallback(
    async (skip = false) => {
      if (submitting || submittedRef.current) return;
      submittedRef.current = true;
      setSubmitting(true);

      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;
      const timeStr = formatSecondsToClock(elapsedRef.current);
      const stageId = sessionStorage.getItem(STAGE_ROW_KEY);

      let stage5Score = 0;
      if (!skip) {
        stage5Score = questions.reduce((sum, q, idx) => {
          const choiceKey = selections[idx];
          if (!choiceKey) return sum;
          return sum + getImageOnlyPointsForChoice(q, choiceKey, pointsPerQuestion);
        }, 0);
        setStage5Points(stage5Score);
      }

      const finalTotal = otherStagesTotalRef.current + stage5Score;

      if (!isDemoBypass && backendBase && adminToken && stageId) {
        try {
          await saveGameStageAndReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            stageId,
            stageNumber: STAGE_NUM,
            score: stage5Score,
            time: timeStr,
            total_score: finalTotal,
            currentQues: skip ? answeredCount : questions.length,
          });
          await fetch(`${backendBase}/welcomeStageComplete`, {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${adminToken}`,
            },
            body: JSON.stringify({
              userId: uid,
              sessionId,
              organizationId,
              stageId,
              time: timeStr,
              total_score: finalTotal,
              points: finalTotal,
            }),
          });
          await updateExternalReport({
            backendBase,
            adminToken,
            storedUser,
            isDemoBypass,
            points: finalTotal,
            time: timeStr,
            stageId,
          });
        } catch (err) {
          console.error("Stage5 finish:", err);
        }
      }

      clearStageActive(STAGE_NUM);
      markStageCompleted(STAGE_NUM, storedUser);

      await showStageCompleteCelebration({
        stageNumber: STAGE_NUM,
        skip,
        score: stage5Score,
      });

      if (isDemoBypass) {
        window.location.href = String(process.env.REACT_APP_BASE_URL || "").trim() || "/";
        return;
      }
      navigateAfterCelebration(navigate, "/leaderboard");
    },
    [
      submitting,
      storedUser,
      isDemoBypass,
      backendBase,
      adminToken,
      questions,
      selections,
      pointsPerQuestion,
      answeredCount,
      navigate,
    ]
  );

  const handleSkip = useCallback(() => {
    finishStage(true);
  }, [finishStage]);

  const showHelp = () => {
    fireStageHelpSwal({
      rules: getStageRulesFromTheme(themeData, "image_only_rules"),
      themeData,
    });
  };

  useEffect(() => {
    let cancelled = false;

    async function loadStageFromDb() {
      if (themeStatus !== "succeeded") return;

      if (!questions.length) {
        if (!cancelled) setDbReady(true);
        return;
      }

      const uid = storedUser.userId || storedUser.userid || storedUser.id;
      const sessionId = storedUser.sessionId;
      const organizationId = storedUser.organizationId;

      if (isDemoBypass || !backendBase || !adminToken) {
        if (!cancelled) setDbReady(true);
        return;
      }

      if (!uid || !sessionId || !organizationId) {
        if (!cancelled) setDbReady(true);
        return;
      }

      try {
        const rowId = sessionStorage.getItem(STAGE_ROW_KEY);
        if (!rowId) {
          if (!cancelled) setDbReady(true);
          return;
        }

        const stageRes = await fetch(`${backendBase}/getGameStage`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: uid,
            sessionId,
            organizationId,
            stageId: rowId,
          }),
        });
        const stageData = await stageRes.json().catch(() => ({}));
        if (cancelled) return;

        if (stageRes.ok && stageData.success && stageData.stage) {
          const st = stageData.stage;
          hydrateCompletedStagesFromRecord(st, {
            stageNumber: STAGE_NUM,
            questionCount: questions.length,
          });
          const resumeRoute = getRedirectForPlayedStage(st, STAGE_NUM, {
            stageNumber: STAGE_NUM,
            questionCount: questions.length,
          });
          if (resumeRoute) {
            navigate(resumeRoute, { replace: true });
            return;
          }

          const dbTimeSec = parseClockToSeconds(st.time);
          const dbTotal = Number(st.total_score) || 0;
          const dbStage5 = Number(st.stage5) || 0;

          baseElapsedRef.current = dbTimeSec;
          elapsedRef.current = dbTimeSec;
          setElapsedSec(dbTimeSec);

          otherStagesTotalRef.current = Math.max(0, dbTotal - dbStage5);
          setStage5Points(dbStage5);
          setDisplayTotalScore(dbTotal);
        }

        setStageActive(STAGE_NUM, storedUser);
        if (!cancelled) setDbReady(true);
      } catch (err) {
        console.error("Stage5 loadStageFromDb:", err);
        if (!cancelled) setDbReady(true);
      }
    }

    loadStageFromDb();
    return () => {
      cancelled = true;
    };
  }, [
    themeStatus,
    questions.length,
    backendBase,
    adminToken,
    isDemoBypass,
    storedUser,
    navigate,
  ]);

  const handleSelect = (questionIndex, choiceKey) => {
    if (submitting) return;
    setSelections((prev) => ({ ...prev, [questionIndex]: choiceKey }));
  };

  const handleSubmit = () => {
    if (!allAnswered || submitting) return;
    finishStage(false);
  };

  if (themeStatus === "loading" || themeStatus === "idle" || !dbReady) {
    return <div className="quiz-stage__loading">Loading game…</div>;
  }

  if (!questions.length) {
    return (
      <div className="quiz-stage__empty">
        No Image Only questions configured. Add them in theme admin under Image Only.
      </div>
    );
  }

  return (
    <GameStageLayout
      title={title}
      subtitle={subtitle}
      themedTextStyle={themedTextStyle}
      elapsedClock={formatSecondsToClock(elapsedSec)}
      points={displayTotalScore}
      onHelp={showHelp}
      onSkip={handleSkip}
      submitting={submitting}
      accent="purple"
      className="game-stage--quiz game-stage--image-only"
      footer={
        <button
          type="button"
          className="game-stage__submit-btn"
          disabled={!allAnswered || submitting}
          onClick={handleSubmit}
        >
          {submitting ? "Submitting…" : "SUBMIT"}
        </button>
      }
    >
      <div className={`quiz-image-cards quiz-image-cards--cols-${cardCols}`}>
        {questions.map((question, idx) => (
          <QuizImageCard
            key={`${question.image || idx}-${idx}`}
            question={question}
            index={idx}
            selectedKey={selections[idx]}
            disabled={submitting}
            onSelect={(choiceKey) => handleSelect(idx, choiceKey)}
          />
        ))}
      </div>
    </GameStageLayout>
  );
}
